import groupBy from 'lodash/groupBy';
import React, { ReactElement, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { twMerge } from 'tailwind-merge';

import { Telemetry } from '@videoblocks/kafka-rest-client';
import { LegacyLoadingSpinner as LoadingSpinner } from '@videoblocks/storywind';

import { showPopUp } from '../../../PopUps/actions/PopUpsActions';
import ShareFolderModal from '../../../PopUps/components/ShareFolderModal';
import {
  CONFIRM_ACTION_MODAL,
  SHARE_FOLDER_MODAL,
} from '../../../PopUps/components/types';
import { notify } from '../../../ReactToastify/containers/ReactToastifyContainer';
import {
  bulkDownloadUpgradeHookExperience,
  nonWaterMarkedBatchDownload,
  performBulkDownloadWithUIExperience,
  showSelectItemsModal,
  watermarkedBatchDownload,
} from '../../../app/Search/components/folders/BulkDownloadUtilites';
import { ContentClass } from '../../../app/Shared/enums';
import { selectUserSubscriptionUniqueId } from '../../../auth/AuthSelectors';
import SiteConstants from '../../../common/SiteConstants/SiteConstants';
import CopyToFolderButton from '../../MemberBin/CopyToFolderButton';
import EditableFolderTitle from '../../MemberBin/EditableFolderTitle';
import {
  deleteFolderContent,
  fetchFolderContent,
  filterKeywordChanged,
  reorderFolderContent,
  saveReorderFolderContent,
  toggleSelectAll,
} from '../../MemberBin/FolderActions';
import {
  addNewFolderWithStockItem,
  addStockItemsToFolder,
} from '../../MemberBins/actions/FoldersActions';
import { selectMemberBin } from '../../MemberBins/selectors/foldersSelectors';
import FolderContentTabs from './FolderContentTabs';
import NoContentHero from './NoContentHero';

import './FolderDetailPage.less';

const assetsCommonImagesUrl = (window as any).__ASSETS_COMMON_IMAGES_URL__;

export const FolderDetailPageContainer = (): ReactElement => {
  const {
    binUniqueId,
    loading,
    binItems,
    similarContentItems,
    features,
    binName,
    fullBinItems,
    hasUserSubscription,
    canUseCsvDownloader,
    removedStockItems,
    failed,
  } = useSelector(selectMemberBin);
  const subscriptionUniqueId = useSelector(selectUserSubscriptionUniqueId);
  const dispatch = useDispatch();
  const [inReorderingMode, setInReorderingMode] = useState(false);
  const [currentTabContentClass, setCurrentTabContentClass] =
    useState<ContentClass>(null);

  useEffect(() => {
    // @ts-ignore
    dispatch(fetchFolderContent(binUniqueId));
  }, []);

  if (loading) {
    return (
      <div className="px-auto flex items-center justify-center w-full h-screen py-12">
        <LoadingSpinner />
      </div>
    );
  }
  if (!loading && failed) {
    return (
      <div className="px-auto flex items-center justify-center w-full h-screen py-12">
        <NoContentHero />
      </div>
    );
  }

  const getSelectedItems = () => {
    return binItems.filter((binItem) => binItem.checked);
  };

  const bulkDownloadClickHandler = () => {
    Telemetry.increment('favorite.bulk-download');
    if (!hasUserSubscription) {
      performBulkDownloadWithUIExperience(
        dispatch,
        binName,
        getSelectedItems(),
        watermarkedBatchDownload
      );
      // @ts-ignore
    } else if (!features.downloadPermissions.downloadLimits) {
      performBulkDownloadWithUIExperience(
        dispatch,
        binName,
        getSelectedItems(),
        nonWaterMarkedBatchDownload
      );
    } else {
      bulkDownloadUpgradeHookExperience(subscriptionUniqueId);
    }
  };

  const startReordering = () => {
    return () => {
      setInReorderingMode(true);
    };
  };

  const saveReordering = () => {
    return () => {
      setInReorderingMode(false);

      dispatch(
        // @ts-ignore
        saveReorderFolderContent(
          binUniqueId,
          fullBinItems.map((binItem) => binItem.stockItem)
        )
      );
    };
  };

  const selectAllDeselectAllActiveClass = (allSelected) => {
    return allSelected
      ? 'gridFolderContainer-cancelButton'
      : 'gridFolderContainer-textButton';
  };

  const selectDeselectAllHandler = (allSelected) => {
    dispatch(
      toggleSelectAll({
        contentClass: currentTabContentClass,
        select: !allSelected,
      })
    );
  };

  const renderSelectAllDeselectAllButton = () => {
    const binItemsOfTabType = binItems.filter(
      (binItem) => binItem.stockItem.contentClass === currentTabContentClass
    );
    const allInCurrentTabSelected =
      binItemsOfTabType.length > 0 &&
      binItemsOfTabType.every((binItem) => binItem.checked);

    return (
      <button
        className={selectAllDeselectAllActiveClass(allInCurrentTabSelected)}
        disabled={inReorderingMode}
        onClick={() => selectDeselectAllHandler(allInCurrentTabSelected)}
      >
        {allInCurrentTabSelected ? 'Cancel' : 'Select All'}
      </button>
    );
  };

  const shareFolderHandler = () => {
    Telemetry.increment('folders.sharePopUp');
    dispatch(showPopUp(SHARE_FOLDER_MODAL));
  };

  const deleteItems = () => {
    const selectedItems = getSelectedItems();
    const selectedItemsByContentClass = groupBy(
      selectedItems,
      'stockItem.contentClass'
    );

    if (selectedItems.length > 0) {
      const itemWord = selectedItems.length === 1 ? 'item' : 'items';

      const bullets = [
        [ContentClass.Video, 'video'],
        [ContentClass.Audio, 'audio'],
        [ContentClass.Image, 'image'],
      ]
        .map(([cc, name]) => {
          const count = selectedItemsByContentClass[cc]?.length || 0;
          return count ? `${count} ${name} item${count > 1 ? 's' : ''}` : null;
        })
        .filter(Boolean);

      dispatch(
        showPopUp(CONFIRM_ACTION_MODAL, {
          title: 'Are you sure?',
          message: (
            <>
              <p>
                You are about to delete {selectedItems.length} {itemWord}.
              </p>
              <ul className="ml-6">
                {bullets.map((message) => (
                  <li key={message}>{message}</li>
                ))}
              </ul>
              <p>You will not be able to undo this action.</p>
            </>
          ),
          buttonText: `Delete ${selectedItems.length} ${itemWord}`,
          buttonAction: () => {
            dispatch(
              // @ts-ignore
              deleteFolderContent(
                binUniqueId,
                selectedItems.map((binItem) => binItem.stockItem)
              )
            );
            notify(
              `Deleting ${selectedItems.length} ${itemWord} from current folder`
            );
          },
        })
      );
    } else {
      showSelectItemsModal(dispatch);
    }
  };

  const createAndCopySelectedStockItemsToFolder = (newName) => {
    const selectedStockItems = getSelectedItems().map(
      (binItem) => binItem.stockItem
    );
    // @ts-ignore
    dispatch(addNewFolderWithStockItem(newName, selectedStockItems));
  };

  const copySelectedStockItemsToFolder = (bin) => {
    dispatch(
      // @ts-ignore
      addStockItemsToFolder(
        bin.uniqueId,
        getSelectedItems().map((binItem) => binItem.stockItem),
        {}
      )
    );
    notify(`Added to "${bin.name}"`);
  };

  const handleReorganizationDrop = () => {
    return (
      draggedContentId: number,
      destinationContentId: number,
      shouldInsertBefore: boolean
    ) => {
      dispatch(
        reorderFolderContent(
          draggedContentId,
          destinationContentId,
          shouldInsertBefore
        )
      );
    };
  };

  const renderRemoveWatermarksMessage = () => {
    if (binName === 'My Previews' && !hasUserSubscription) {
      return (
        <p className="w-80">
          Say goodbye to watermarks and start downloading high-quality clips.{' '}
          <a href={'/pricing'}>Subscribe Now →</a>
        </p>
      );
    }
  };

  const renderIconControls = () => {
    return (
      <div
        className={twMerge(
          'inline-block h-12 border border-solid border-gray-400 rounded',
          inReorderingMode && 'bg-gray-300'
        )}
      >
        <button
          className="gridFolderContainer-iconButton"
          disabled={inReorderingMode}
          id="downloadItems"
          onClick={bulkDownloadClickHandler}
        >
          <img
            className="w-6 h-6"
            src={assetsCommonImagesUrl + '/member/folders/download.svg'}
            alt={!hasUserSubscription ? 'Download Previews' : 'Download'}
            title={!hasUserSubscription ? 'Download Previews' : 'Download'}
            width={6}
            height={6}
          />
        </button>

        <CopyToFolderButton
          copySelectedStockItemsToFolder={copySelectedStockItemsToFolder}
          createAndCopySelectedStockItemsToFolder={
            createAndCopySelectedStockItemsToFolder
          }
          disabled={inReorderingMode}
          selectedItems={getSelectedItems()}
          showSelectItemsModal={() => showSelectItemsModal(dispatch)}
        />

        <button
          className="gridFolderContainer-iconButton"
          disabled={inReorderingMode}
          id="trashItems"
          onClick={deleteItems}
        >
          <img
            alt="Delete"
            title="Delete"
            className="w-6 h-6"
            src={assetsCommonImagesUrl + '/member/folders/trash.svg'}
            width={6}
            height={6}
          />
        </button>

        <button
          className="gridFolderContainer-iconButton"
          disabled={inReorderingMode}
          id="shareFolder"
          onClick={shareFolderHandler}
        >
          <img
            className="w-6 h-6"
            src={assetsCommonImagesUrl + '/member/folders/share.svg'}
            alt="Send Link"
            title="Send Link"
            width={6}
            height={6}
          />
        </button>
      </div>
    );
  };

  // FIXME: break this out into separate component
  const renderControls = () => {
    return (
      <div>
        {fullBinItems.length > 0 && (
          <input
            className="h-9 w-full px-3 py-2 mb-4 bg-white border border-gray-300 border-solid rounded"
            id="search-folder-input"
            onChange={(e) => dispatch(filterKeywordChanged(e.target.value))}
            placeholder="Search Folder..."
            type="text"
          />
        )}

        <div className="gridFolderContainer-seperableControls flex flex-wrap justify-between w-full space-x-2">
          <button
            className={
              inReorderingMode
                ? 'gridFolderContainer-doneButton'
                : 'gridFolderContainer-reorderButton'
            }
            onClick={inReorderingMode ? saveReordering() : startReordering()}
          >
            {inReorderingMode ? 'Done' : 'Reorder'}
          </button>

          {renderSelectAllDeselectAllButton()}

          {renderIconControls()}
        </div>
      </div>
    );
  };

  // Admin only features

  const buildCsvFromFolderContents = () => {
    return fullBinItems
      .map((binItem) => {
        return (binItem.stockItem.detailsUrl as string).split('/')[3];
      })
      .join('\n');
  };

  const renderCsvDownloadButton = () => {
    if (canUseCsvDownloader) {
      return (
        <a
          className="folder-toCsv"
          href={
            'data:text/csv;charset=utf-8;base64,' +
            btoa(buildCsvFromFolderContents())
          }
          download={binName.replace(' ', '-') + '.csv'}
        >
          CSV
        </a>
      );
    }

    return null;
  };

  const renderAdminOnlyTools = () => {
    return renderCsvDownloadButton();
  };

  return (
    <div className="gridFolderContainer flex flex-col px-4 mx-auto max-w-screen-2xl">
      <ShareFolderModal
        shareUrl={`${
          window.location.origin
        }${SiteConstants.buildInstance().getFoldersFeedbackBasePath()}/${binUniqueId}`}
        folderUniqueId={binUniqueId}
      />
      <div className="mt-4">
        <a href={SiteConstants.buildInstance().getFoldersBasePath()}>
          Back to Folders
        </a>
      </div>
      <div className="md:flex-nowrap flex flex-wrap justify-between my-4">
        <div className="gridFolderContainer-heading">
          <EditableFolderTitle />
          <h3 className="my-2 text-base font-normal">
            <span data-testid="item-count" className="font-bold">
              {removedStockItems.length + binItems.length} Items
            </span>
          </h3>
          {renderRemoveWatermarksMessage()}
        </div>

        {renderControls()}
      </div>

      {renderAdminOnlyTools()}

      <FolderContentTabs
        allowEmptyTabs
        allowSelections
        allowDownloads
        folderItems={binItems}
        similarItems={similarContentItems}
        onReorderDrop={handleReorganizationDrop()}
        inReorderingMode={inReorderingMode}
        onTabChange={setCurrentTabContentClass}
        removedStockItems={removedStockItems}
      />
    </div>
  );
};

export default FolderDetailPageContainer;
